import { useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Drawer as Base } from '@base-ui/react/drawer';
import { X } from 'lucide-react';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { DialogCloseProvider, useDeferredClose, type CloseRequest } from './deferred-close';
import { returnFocusUnlessMoved } from './focus';
import { Icon } from './icons';
import { IconButton } from './IconButton';

/**
 * Modal sheet on Base UI's Drawer (P18): the bottom sheet of the phone (grab handle, follows the finger 1:1, closes on a
 * fast flick or past half its height, rests on `snapPoints`), or the navigation drawer from the left edge. A named
 * `role="dialog"` with the focus trapped and the page behind locked; a swipe, Escape, the backdrop and the close button
 * close it. Rendered open like `Dialog`: the caller mounts it to open it and unmounts it in `onClose`, which runs once
 * the exit is over (and the exit starts from wherever the finger let go). Styled by the `os-drawer*` classes.
 *
 * Fields in the sheet stay above the on-screen keyboard (`--drawer-keyboard-inset`, 0px without a keyboard).
 */
export function Drawer({
  title,
  description,
  closeLabel,
  onClose,
  onCloseRequest,
  children,
  footer,
  className,
  snapPoints,
  side = 'bottom',
  variant = 'sheet',
  initialFocus,
  finalFocus,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Accessible name of the close button; without it the sheet has none (a dialog closes by Cancel, a swipe or Escape). */
  closeLabel?: string;
  onClose: () => void;
  /** Asked before every close; `false` keeps it open. */
  onCloseRequest?: CloseRequest;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Rest positions as fractions of the screen height (0.6 and 0.92 for the board sheet). With a `max-h` on `className` for one position. */
  snapPoints?: number[];
  side?: 'bottom' | 'left';
  /** `dialog`: the title is a heading like a `Dialog`'s (AdaptiveDialog); `sheet`: the small caption of a sheet. */
  variant?: 'sheet' | 'dialog';
  initialFocus?: false;
  finalFocus?: false;
}) {
  const popup = useRef<HTMLDivElement>(null);
  const [opener] = useState(() => document.activeElement);
  const { open, request, complete } = useDeferredClose(onClose, { onCloseRequest });
  const left = side === 'left';
  const heading = variant === 'dialog';
  return (
    <DialogCloseProvider value={request}>
      <Base.Root
        open={open}
        modal
        snapPoints={snapPoints}
        swipeDirection={left ? 'left' : 'down'}
        onOpenChange={(next, details) => {
          if (!next && !request()) details.cancel();
        }}
        onOpenChangeComplete={complete}
      >
        <Base.VirtualKeyboardProvider>
          <Base.Portal>
            <Base.Backdrop
              data-backdrop=""
              {...{ [CANVAS_UI]: '' }}
              className="os-drawer-backdrop"
            />
            <Base.Viewport
              {...{ [CANVAS_UI]: '' }}
              className={cn('os-drawer-viewport', left && 'os-drawer-left')}
            >
              <Base.Popup
                ref={popup}
                {...{ [CANVAS_UI]: '' }}
                initialFocus={initialFocus ?? popup}
                finalFocus={finalFocus ?? returnFocusUnlessMoved(popup, opener)}
                className={cn(
                  'os-drawer bg-chrome font-sans text-sm text-fg shadow-float outline-none',
                  left ? 'os-drawer-left' : 'rounded-t-3xl',
                  className,
                )}
              >
                {!left && (
                  <div
                    aria-hidden="true"
                    data-testid="sheet-handle"
                    className="mx-auto flex h-6 w-16 shrink-0 items-center justify-center"
                  >
                    <span className="block h-1 w-9 rounded-full bg-border-strong" />
                  </div>
                )}
                <div
                  className={cn(
                    'flex shrink-0 items-center justify-between gap-2 px-4',
                    left && 'pt-3',
                    heading ? 'pb-3' : 'pb-2',
                  )}
                >
                  <Base.Title
                    className={
                      heading
                        ? 'font-display text-title font-semibold text-fg'
                        : 'text-xs font-medium text-fg-muted'
                    }
                  >
                    {title}
                  </Base.Title>
                  {closeLabel && (
                    <IconButton
                      label={closeLabel}
                      icon={<Icon icon={X} size={16} />}
                      size="sm"
                      onClick={() => void request()}
                    />
                  )}
                </div>
                <Base.Content className="os-drawer-scroll px-4">
                  {description && (
                    <Base.Description className="mb-4 text-sm text-fg-muted">
                      {description}
                    </Base.Description>
                  )}
                  {children}
                </Base.Content>
                {footer && (
                  <div className="flex shrink-0 justify-end gap-2 px-4 pb-[max(1rem,var(--safe-bottom))] pt-3">
                    {footer}
                  </div>
                )}
              </Base.Popup>
            </Base.Viewport>
          </Base.Portal>
        </Base.VirtualKeyboardProvider>
      </Base.Root>
    </DialogCloseProvider>
  );
}

export type DrawerProps = ComponentProps<typeof Drawer>;
